import React, { useState, useEffect, useCallback } from "react";
import config from "../config";
import { devLog } from "../utils/devLog";

export default function AdminPublicLeagues({ currentUser, adminToken }) {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newLeague, setNewLeague] = useState({
    name: "",
    scoring_type: "STANDARD",
    draft_type: "SNAKE",
    salary_cap: 200,
    faab_budget: 100,
    use_faab: false,
    roster_unlock_day: "wednesday",
    team_count: 10
  });
  const [createMsg, setCreateMsg] = useState("");

  // Fetch all leagues
  const fetchLeagues = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${config.API_URL}/admin/leagues`, {
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) throw new Error("Failed to fetch leagues");
      const data = await res.json();
      setLeagues(data);
    } catch (err) {
      setError("Could not load leagues");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) fetchLeagues();
  }, [adminToken, fetchLeagues]);

  // Create new public league
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateMsg("");
    setError("");
    try {
      const params = new URLSearchParams({
        name: newLeague.name,
        scoring_type: newLeague.scoring_type,
        draft_type: newLeague.draft_type,
        salary_cap: newLeague.salary_cap,
        faab_budget: newLeague.faab_budget,
        use_faab: newLeague.use_faab,
        roster_unlock_day: newLeague.roster_unlock_day,
        team_count: newLeague.team_count
      });
      // Change endpoint from /admin/public-leagues to /admin/leagues
      const res = await fetch(`${config.API_URL}/admin/leagues?${params.toString()}`, {
        method: "POST",
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to create league");
      }
      
      await res.json();
      setCreateMsg("Public league created successfully!");
      
      setNewLeague({ ...newLeague, name: "" });
      fetchLeagues();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete any league
  const handleDelete = async (leagueId) => {
    if (!window.confirm("Delete this league? This cannot be undone.")) return;
    setError("");
    try {
      devLog(`Attempting to delete league ${leagueId}...`);

      const res = await fetch(`${config.API_URL}/admin/leagues/${leagueId}`, {
        method: "DELETE",
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      devLog("Delete response status:", res.status);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error("Delete error:", errorText);
        
        // Check if it's a token expiration error
        if (res.status === 401 && errorText.includes("admin token")) {
          setError("Admin session expired. Please log out and log back in as admin.");
          return;
        }
        
        throw new Error(`Failed to delete league: ${res.status} ${errorText}`);
      }
      
      devLog("League deleted successfully");
      fetchLeagues();
    } catch (err) {
      console.error("Delete league error:", err);
      setError(`Could not delete league: ${err.message}`);
    }
  };



  return (
    <div style={{ maxWidth: 700, margin: "40px auto", padding: 32, background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px #0001" }}>
      <h2 style={{ color: "#2563eb" }}>League Management</h2>
      {loading ? <div>Loading...</div> : (
        <>
          <h3 style={{ color: "#059669" }}>Existing Leagues</h3>
          {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
          <ul style={{ listStyle: "none", padding: 0 }}>
            {leagues.map(lg => (
              <li key={lg.id} style={{ marginBottom: 10, borderBottom: "1px solid #e0e7ff", paddingBottom: 8 }}>
                <b>{lg.name}</b> (ID: {lg.id}) — {lg.scoring_type}, {lg.draft_type}

                , Teams: {lg.team_count}
                <button onClick={() => handleDelete(lg.id)} className="ffb-btn" style={{ marginLeft: 16, background: "#dc2626" }}>Delete</button>
                {lg.is_public ? <span style={{ color: '#059669', marginLeft: 8 }}>[Public]</span> : <span style={{ color: '#64748b', marginLeft: 8 }}>[Private]</span>}
              </li>
            ))}
            {leagues.length === 0 && <li style={{ color: '#64748b' }}>No leagues found.</li>}
          </ul>
          <h3 style={{ color: "#2563eb", marginTop: 32 }}>Create New Public League</h3>
          <form onSubmit={handleCreate} style={{ marginBottom: 18 }}>
            <input
              type="text"
              placeholder="League name"
              value={newLeague.name}
              onChange={e => setNewLeague({ ...newLeague, name: e.target.value })}
              style={{ width: 220, padding: 8, borderRadius: 6, border: "1px solid #b6c3e0", marginRight: 10 }}
              required
            />
            <input
              type="number"
              placeholder="Teams"
              value={newLeague.team_count}
              onChange={e => setNewLeague({ ...newLeague, team_count: Number(e.target.value) })}
              style={{ width: 80, padding: 8, borderRadius: 6, border: "1px solid #b6c3e0", marginRight: 10 }}
              min={2}
              max={20}
              required
            />
            <button type="submit" className="ffb-btn" style={{ background: "#059669", padding: 10, borderRadius: 6 }}>
              Create
            </button>
            {createMsg && <span style={{ color: '#059669', marginLeft: 12 }}>{createMsg}</span>}
          </form>
        </>
      )}
    </div>
  );
} 